import React, { useEffect, useState } from 'react'
import {
  FilterSection,
  FilterSubsection,
  FieldLabelWithTooltip,
  AdvancedDropdown,
  type AdvancedDropdownOption,
  ToggleSwitch,
} from '@popety-io/ui'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import type { LandSearchFilters } from '../../../Lands/types/landFilters.types'
import { getAllTags } from '../../../Lands/services/landTagsService'
import { useLandsFavorites } from '@/store/hooks'

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
`

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`

interface UserFiltersProps {
  filters: LandSearchFilters
  updateFilter: <K extends keyof LandSearchFilters>(
    key: K,
    value: LandSearchFilters[K]
  ) => void
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  updateFilter,
}) => {
  const { t } = useTranslation('common')
  const { favorites: landFavorites } = useLandsFavorites()

  const [availableTags, setAvailableTags] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchUserTags()
  }, [])

  const fetchUserTags = async () => {
    try {
      setLoading(true)
      const tags = await getAllTags()
      // The API returns LandTagRelation[]
      setAvailableTags(tags as any)
    } catch (error) {
      console.error('Failed to fetch user tags:', error)
      setAvailableTags([])
    } finally {
      setLoading(false)
    }
  }

  // Get unique tags from the response and convert to dropdown options
  const uniqueTagsMap = new Map<
    string,
    { id: string; value: string; color: string }
  >()

  // Handle LandTagRelation structure
  if (availableTags && Array.isArray(availableTags)) {
    availableTags.forEach((item: any) => {
      if (item.tag) {
        // LandTagRelation structure
        if (!uniqueTagsMap.has(item.tag.id)) {
          uniqueTagsMap.set(item.tag.id, item.tag)
        }
      } else if (item.id && item.value) {
        // Tag structure
        if (!uniqueTagsMap.has(item.id)) {
          uniqueTagsMap.set(item.id, item)
        }
      }
    })
  }

  const uniqueTagOptions: AdvancedDropdownOption[] = Array.from(
    uniqueTagsMap.values()
  ).map((tag) => ({
    id: tag.id,
    label: tag.value,
    icon: (
      <span
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: tag.color,
          display: 'inline-block',
        }}
      />
    ),
  }))

  // Check if favorites filter is active
  const isFavoritesFilterActive =
    filters.land_ids && filters.land_ids.length > 0

  // Extract active favorite land IDs
  const favoriteLandIds = landFavorites
    .filter((fav) => fav.active)
    .map((fav) => fav.land_id)

  const getUserFiltersCount = () => {
    let count = 0
    if (filters.tags?.length) count++
    if (isFavoritesFilterActive) count++
    return count
  }

  const handleFavoritesToggle = (checked: boolean) => {
    if (checked) {
      // When toggling ON, set land_ids to favorite land IDs
      updateFilter(
        'land_ids',
        favoriteLandIds.length > 0 ? favoriteLandIds : undefined
      )
    } else {
      // When toggling OFF, clear land_ids filter
      updateFilter('land_ids', undefined)
    }
  }

  const handleTagsChange = (selectedTagIds: string | string[]) => {
    const tagIds = Array.isArray(selectedTagIds)
      ? selectedTagIds
      : [selectedTagIds]

    // Simply update the tags filter with the selected tag IDs
    updateFilter('tags', tagIds.length ? tagIds : undefined)
  }

  // Get currently selected tags
  const selectedTags = filters.tags || []

  return (
    <FilterSection
      title={t('filterSectionTitles.userFilters')}
      icon={
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20 7h-9m3 10H9m8-5H9m13-3.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-16 5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm8 5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        </svg>
      }
      count={getUserFiltersCount() + '/2'}
      active={getUserFiltersCount() > 0}
    >
      <FilterSubsection>
        <FieldLabelWithTooltip
          label={t('filterFieldLabels.favorites')}
          tooltip={t('filterTooltips.land.favorites')}
        />
        <ToggleRow>
          <ToggleLabel>
            {isFavoritesFilterActive
              ? t('filterLabels.showOnlyFavorites')
              : t('filterLabels.showAllLands')}
          </ToggleLabel>
          <ToggleSwitch
            checked={isFavoritesFilterActive || false}
            onChange={handleFavoritesToggle}
            size="small"
            disabled={favoriteLandIds.length === 0}
          />
        </ToggleRow>
        {favoriteLandIds.length === 0 && (
          <div style={{ padding: '8px', color: '#6b6b6b', fontSize: '12px' }}>
            {t('filterMessages.noFavoritesAvailable')}
          </div>
        )}
      </FilterSubsection>
      <FilterSubsection>
        <FieldLabelWithTooltip
          label={t('filterFieldLabels.myTags')}
          tooltip={t('filterTooltips.land.userTags')}
        />
        {loading ? (
          <div style={{ padding: '8px', color: '#6b6b6b' }}>
            Loading tags...
          </div>
        ) : uniqueTagOptions.length > 0 ? (
          <AdvancedDropdown
            value={selectedTags}
            onChange={handleTagsChange}
            options={uniqueTagOptions}
            placeholder="Select tags"
            multiple
            searchable
            clearable
            size="small"
            width="100%"
            emptyMessage="No tags available"
          />
        ) : (
          <div style={{ padding: '8px', color: '#6b6b6b' }}>
            No tags available. Create tags from the land details panel.
          </div>
        )}
      </FilterSubsection>
    </FilterSection>
  )
}
